import axios from 'axios';
import { apiClient, authApiClient } from './api-client';

// ── Types (DOMAIN_EXPERT_AGENTS_PLAN.md §8.9) ────────────────────────────

export type ExpertStatus = 'invited' | 'active' | 'disabled';
export type InviteState = 'pending' | 'expired' | 'none';

export interface ExpertItem {
  id: string;
  email: string;
  full_name: string;
  status: ExpertStatus;
  invite_state: InviteState;
  invite_expires_at: string | null;
  sign_in_methods: Array<'password' | 'google'>;
  signup_source: 'invite' | 'google_auto_join';
  last_login_at: string | null;
  last_seen_at: string | null;
  created_at: string;
  created_by: {
    type: 'tenant_admin' | 'super_admin' | 'internal_service' | 'self_signup';
    email: string | null;
  };
}

export interface ExpertList {
  items: ExpertItem[];
  total: number;
  counts: { invited: number; active: number; disabled: number };
}

export interface InviteResult {
  invite_url: string;
  invite_expires_at: string;
  email_sent: boolean;
}

export interface CreateExpertResult {
  expert: ExpertItem;
  invite_url: string | null;
  invite_expires_at: string | null;
  email_sent: boolean;
}

export interface ResetResult {
  reset_url: string;
  expires_at: string;
  email_sent: boolean;
}

export interface SignInSettings {
  google_sso_enabled: boolean;
  allowed_google_domains: string[];
  auto_join_enabled: boolean;
  password_login_enabled: boolean;
  login_url: string;
  app_subdomain: string;
}

export interface LoginLink {
  login_url: string;
  app_subdomain: string;
}

export interface AppSettingsEffective {
  name: string;
  short_name: string;
  icon_url: string | null;
  primary_color: string;
  background_color: string;
  welcome_message: string;
  input_placeholder: string;
  suggested_prompts: string[];
}

export interface AppSettings {
  app_name: string | null;
  app_short_name: string | null;
  app_icon_url: string | null;
  primary_color: string | null;
  welcome_message: string | null;
  suggested_prompts: string[];
  app_subdomain: string;
  effective: AppSettingsEffective;
  config_version: string;
}

export type AppSettingsUpdate = Partial<
  Pick<AppSettings, 'app_name' | 'app_short_name' | 'app_icon_url' | 'primary_color' | 'welcome_message' | 'suggested_prompts'>
>;

export type TrainingDocStatus = 'processing' | 'ready' | 'failed';

export interface TrainingDoc {
  id: string;
  title: string;
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  status: TrainingDocStatus;
  error: string | null;
  created_at: string;
  uploaded_by_email: string | null;
}

export interface DocList {
  items: TrainingDoc[];
  total: number;
  counts: { processing: number; ready: number; failed: number };
}

export interface SavedAnswer {
  id: string;
  question: string;
  answer: string;
  source: 'manual' | 'flag_correction';
  answer_flag_id: string | null;
  created_at: string;
  updated_at: string;
  created_by_email: string | null;
}

export interface AnswerList {
  items: SavedAnswer[];
  total: number;
}

export interface AgentSettings {
  custom_instructions: string | null;
  no_answer_message: string | null;
  default_no_answer_message: string;
  /** The tenant homepage the expert app shows at the top of a new chat; null = none (welcome message). */
  homepage_id: string | null;
}

export type AgentSettingsUpdate = Partial<Pick<AgentSettings, 'custom_instructions' | 'no_answer_message' | 'homepage_id'>>;

export type FlagReason = 'incorrect' | 'incomplete' | 'outdated' | 'unclear' | 'other';
export type FlagStatus = 'open' | 'resolved' | 'dismissed' | 'withdrawn';
export type FlagTab = 'open' | 'done';

export interface AdminFlag {
  id: string;
  question: string;
  answer: string;
  sources: unknown[];
  reason_category: FlagReason;
  comment: string | null;
  suggested_answer: string | null;
  status: FlagStatus;
  resolution_note: string | null;
  resolution_question_id: string | null;
  flagged_by: { id: string; email: string; name: string | null };
  conversation_id: string | null;
  message_id: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by_email: string | null;
}

export interface AdminFlagDetail extends AdminFlag {
  context: Array<{ role: 'user' | 'assistant'; content: string; created_at: string }>;
}

export interface FlagList {
  items: AdminFlag[];
  total: number;
  counts: { open: number; done: number };
}

export interface FlagCounts {
  open: number;
}

export interface FixResult {
  flag: AdminFlag;
  saved_answer: SavedAnswer;
}

export interface PageParams {
  search?: string;
  skip?: number;
  limit?: number;
}

const clean = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));

// ── Query keys ───────────────────────────────────────────────────────────

export const expertKeys = {
  all: ['expert'] as const,
  experts: (params?: object) => ['expert', 'experts', params ?? {}] as const,
  loginLink: () => ['expert', 'login-link'] as const,
  signIn: () => ['expert', 'sign-in'] as const,
  app: () => ['expert', 'app'] as const,
  agentSettings: () => ['expert', 'agent-settings'] as const,
  documents: (params?: object) => ['expert', 'documents', params ?? {}] as const,
  answers: (params?: object) => ['expert', 'answers', params ?? {}] as const,
  flags: (params?: object) => ['expert', 'flags', params ?? {}] as const,
  flagCounts: () => ['expert', 'flag-counts'] as const,
  flag: (id: string) => ['expert', 'flag', id] as const,
};

// ── Errors ───────────────────────────────────────────────────────────────

const FRIENDLY_MESSAGES: Record<string, string> = {
  duplicate_email: 'This email already has access.',
  embedding_unavailable: "We couldn't save this right now. Please try again in a moment.",
  llm_unavailable: "We couldn't save this right now. Please try again in a moment.",
  file_type_not_supported: 'Only PDF, TXT and MD files are supported.',
  file_too_large: 'One or more files are too large.',
  feature_not_enabled: 'Domain Expert Agents is not turned on for your account.',
  no_sign_in_method: 'Users need at least one way to sign in.',
  auto_join_requires_google: 'Automatic joining needs Google sign-in to be turned on.',
};

export function isRequestCancelled(error: unknown): boolean {
  return axios.isCancel(error);
}

export function getExpertErrorCode(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const code = (detail as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

export function getExpertErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const code = getExpertErrorCode(error);
  if (code && FRIENDLY_MESSAGES[code]) return FRIENDLY_MESSAGES[code];
  if (response?.status === 413) return FRIENDLY_MESSAGES.file_too_large;
  if (response?.status === 415) return FRIENDLY_MESSAGES.file_type_not_supported;

  const detail = response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: unknown };
    if (typeof first?.msg === 'string') return first.msg.replace(/^Value error, /, '');
  }
  return fallback;
}

// ── client-mgmt-api ──────────────────────────────────────────────────────

export const domainExpertApi = {
  listExperts: async (params: PageParams & { status?: ExpertStatus } = {}): Promise<ExpertList> => {
    const { data } = await authApiClient.get<ExpertList>('/tenant/domain-experts', { params: clean({ ...params }) });
    return data;
  },

  createExpert: async (body: { email: string; full_name: string }): Promise<CreateExpertResult> => {
    const { data } = await authApiClient.post<CreateExpertResult>('/tenant/domain-experts', { ...body, send_invite: true });
    return data;
  },

  updateExpert: async (id: string, body: { full_name?: string; email?: string; status?: ExpertStatus }): Promise<ExpertItem> => {
    const { data } = await authApiClient.patch<ExpertItem>(`/tenant/domain-experts/${id}`, body);
    return data;
  },

  deleteExpert: async (id: string): Promise<void> => {
    await authApiClient.delete(`/tenant/domain-experts/${id}`);
  },

  sendInvite: async (id: string, sendEmail: boolean): Promise<InviteResult> => {
    const { data } = await authApiClient.post<InviteResult>(`/tenant/domain-experts/${id}/invite`, { send_email: sendEmail });
    return data;
  },

  cancelInvite: async (id: string): Promise<void> => {
    await authApiClient.delete(`/tenant/domain-experts/${id}/invite`);
  },

  resetPassword: async (id: string, sendEmail: boolean): Promise<ResetResult> => {
    const { data } = await authApiClient.post<ResetResult>(`/tenant/domain-experts/${id}/password-reset`, { send_email: sendEmail });
    return data;
  },

  revokeSessions: async (id: string): Promise<{ revoked: number }> => {
    const { data } = await authApiClient.post<{ revoked: number }>(`/tenant/domain-experts/${id}/sessions/revoke`);
    return data;
  },

  getLoginLink: async (): Promise<LoginLink> => {
    const { data } = await authApiClient.get<LoginLink>('/tenant/domain-experts/login-link');
    return data;
  },

  getSignInSettings: async (): Promise<SignInSettings> => {
    const { data } = await authApiClient.get<SignInSettings>('/tenant/domain-expert/sign-in');
    return data;
  },

  updateSignInSettings: async (
    body: Partial<Pick<SignInSettings, 'auto_join_enabled' | 'password_login_enabled'>>,
  ): Promise<SignInSettings> => {
    const { data } = await authApiClient.patch<SignInSettings>('/tenant/domain-expert/sign-in', body);
    return data;
  },

  getAppSettings: async (): Promise<AppSettings> => {
    const { data } = await authApiClient.get<AppSettings>('/tenant/domain-expert/app');
    return data;
  },

  updateAppSettings: async (body: AppSettingsUpdate): Promise<AppSettings> => {
    const { data } = await authApiClient.patch<AppSettings>('/tenant/domain-expert/app', body);
    return data;
  },
};

// ── ai-api ───────────────────────────────────────────────────────────────

export const expertAgentApi = {
  getSettings: async (): Promise<AgentSettings> => {
    const { data } = await apiClient.get<AgentSettings>('/tenant/expert-agent/settings');
    return data;
  },

  updateSettings: async (body: AgentSettingsUpdate): Promise<AgentSettings> => {
    const { data } = await apiClient.put<AgentSettings>('/tenant/expert-agent/settings', body);
    return data;
  },

  listDocuments: async (params: PageParams & { status?: TrainingDocStatus } = {}): Promise<DocList> => {
    const { data } = await apiClient.get<DocList>('/tenant/expert-agent/documents', { params: clean({ ...params }) });
    return data;
  },

  uploadDocuments: async (files: File[]): Promise<{ items: TrainingDoc[] }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const { data } = await apiClient.post<{ items: TrainingDoc[] }>('/tenant/expert-agent/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  retryDocument: async (id: string): Promise<TrainingDoc> => {
    const { data } = await apiClient.post<TrainingDoc>(`/tenant/expert-agent/documents/${id}/retry`);
    return data;
  },

  deleteDocument: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenant/expert-agent/documents/${id}`);
  },

  refreshTraining: async (): Promise<{ saved_answers: number; documents: number }> => {
    const { data } = await apiClient.post<{ saved_answers: number; documents: number }>(
      '/tenant/expert-agent/embeddings/regenerate',
    );
    return data;
  },

  listAnswers: async (params: PageParams = {}): Promise<AnswerList> => {
    const { data } = await apiClient.get<AnswerList>('/tenant/expert-agent/answers', { params: clean({ ...params }) });
    return data;
  },

  createAnswer: async (body: { question: string; answer: string }): Promise<SavedAnswer> => {
    const { data } = await apiClient.post<SavedAnswer>('/tenant/expert-agent/answers', body);
    return data;
  },

  updateAnswer: async (id: string, body: { question?: string; answer?: string }): Promise<SavedAnswer> => {
    const { data } = await apiClient.put<SavedAnswer>(`/tenant/expert-agent/answers/${id}`, body);
    return data;
  },

  deleteAnswer: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenant/expert-agent/answers/${id}`);
  },

  listFlags: async (params: PageParams & { status: FlagTab; expert_id?: string }): Promise<FlagList> => {
    const { data } = await apiClient.get<FlagList>('/tenant/expert-agent/flags', { params: clean({ ...params }) });
    return data;
  },

  getFlagCounts: async (): Promise<FlagCounts> => {
    const { data } = await apiClient.get<FlagCounts>('/tenant/expert-agent/flags/counts');
    return data;
  },

  getFlag: async (id: string): Promise<AdminFlagDetail> => {
    const { data } = await apiClient.get<AdminFlagDetail>(`/tenant/expert-agent/flags/${id}`);
    return data;
  },

  fixFlag: async (id: string, body: { question: string; answer: string; note?: string }): Promise<FixResult> => {
    const { data } = await apiClient.post<FixResult>(`/tenant/expert-agent/flags/${id}/fix`, body);
    return data;
  },

  closeFlag: async (id: string, body: { note?: string }): Promise<AdminFlag> => {
    const { data } = await apiClient.post<AdminFlag>(`/tenant/expert-agent/flags/${id}/close`, body);
    return data;
  },

  reopenFlag: async (id: string): Promise<AdminFlag> => {
    const { data } = await apiClient.post<AdminFlag>(`/tenant/expert-agent/flags/${id}/reopen`);
    return data;
  },
};

// ── Display helpers ──────────────────────────────────────────────────────

export const FLAG_REASON_LABELS: Record<FlagReason, string> = {
  incorrect: 'Incorrect',
  incomplete: 'Incomplete',
  outdated: 'Out of date',
  unclear: 'Unclear',
  other: 'Other',
};

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
